import { BookOpenText, FilePenLine, Handshake, PackageCheck, ShieldCheck, PieChart, TrendingDown, Scale, Presentation } from "lucide-react";

export const startupCards = [
  {
    id: "business-plan-generator",
    title: "Startup Business Plan Generator",
    eyebrow: "Editable planner",
    description: "Create a polished multi-page business plan with guided prompts, live preview, PDF export, and editable Word download.",
    path: "/startup/business-plan-generator",
    action: "Create business plan",
    Icon: FilePenLine,
  },
  {
    id: "seis-eis-checker",
    title: "SEIS / EIS Eligibility Checker",
    eyebrow: "Funding eligibility",
    description: "Check whether your UK company qualifies for SEIS or EIS and estimate the income tax and CGT relief available to investors.",
    path: "/startup/seis-eis-checker",
    action: "Check eligibility",
    Icon: ShieldCheck,
  },
  {
    id: "cap-table-builder",
    title: "Cap Table Builder",
    eyebrow: "Founder ownership",
    description: "Model founder splits, options pool, SAFE conversions, and a priced round to see fully-diluted ownership update live.",
    path: "/startup/cap-table-builder",
    action: "Build cap table",
    Icon: PieChart,
  },
  {
    id: "runway-calculator",
    title: "Runway Calculator",
    eyebrow: "Cash runway",
    description: "Project monthly burn against revenue and cost growth to find the month your cash hits zero.",
    path: "/startup/runway-calculator",
    action: "Plan runway",
    Icon: TrendingDown,
  },
  {
    id: "break-even-calculator",
    title: "Break-Even Calculator",
    eyebrow: "Pricing fundamentals",
    description: "Find the units and revenue you need to cover fixed costs and hit a target profit, with live contribution margin.",
    path: "/startup/break-even-calculator",
    action: "Find break-even",
    Icon: Scale,
  },
  {
    id: "pitch-deck-generator",
    title: "Pitch Deck Outline Generator",
    eyebrow: "Investor pitch",
    description: "Fill in a 10-slide skeleton (problem, solution, market, traction, ask) and export the outline as a PDF or copy-paste text.",
    path: "/startup/pitch-deck-generator",
    action: "Build pitch outline",
    Icon: Presentation,
  },
  {
    id: "consulting-contract-generator",
    title: "Consulting Client Contract Generator",
    eyebrow: "Client agreement",
    description: "Create a branded consulting or freelance client contract with guided sections, PDF export, and editable Word download.",
    path: "/startup/consulting-client-contract",
    action: "Create contract",
    Icon: Handshake,
  },
  {
    id: "product-business-management",
    title: "Product Business Management System",
    eyebrow: "All-in-one template",
    description: "Explore an integrated product-business workspace with inventory, purchases, sales, recipes, reports, tasks, employees, and exportable PRD.",
    path: "/startup/product-business-management",
    action: "Open system",
    Icon: PackageCheck,
  },
  {
    id: "startup-planner-coming-soon",
    title: "More Startup Planners",
    eyebrow: "Coming next",
    description: "Future startup tools can include funding pitch pages, launch checklists, customer discovery, and pricing playbooks.",
    path: "/startup/business-plan-generator",
    action: "Start with plan",
    Icon: BookOpenText,
  },
];

export const planSections = [
  { id: "overview", label: "Overview" },
  { id: "vision", label: "Vision & Mission" },
  { id: "business", label: "Business Structure" },
  { id: "client", label: "Ideal Client" },
  { id: "marketing", label: "Marketing" },
  { id: "competitive", label: "Competition" },
  { id: "financial", label: "Financials" },
  { id: "future", label: "Future Plan" },
  { id: "team", label: "Team & Actions" },
];

export const defaultBusinessPlan = () => ({
  companyName: "Northstar Studio",
  tagline: "A focused launch plan for a practical, profitable small business.",
  founderName: "Your Name",
  year: new Date().getFullYear().toString(),
  preparedFor: "Founders, lenders, partners, and advisors",
  contactEmail: "hello@example.co.uk",
  documentColor: "#fffaf8",
  fontColor: "#201b1a",
  accentColor: "#a87d73",
  executiveSummary: "Northstar Studio helps independent brands turn specialist knowledge into clear digital products and premium service offers. The business will launch with a lean operating model, a focused client segment, and a practical marketing system designed to generate predictable leads.",
  vision: "To become a trusted small-business partner for founders who want clarity, calm execution, and commercially useful strategy.",
  mission: "To help clients define their offer, launch faster, and build repeatable systems without unnecessary complexity.",
  values: "Clarity, usefulness, consistency, honest communication, and sustainable growth.",
  legalStructure: "Sole trader or limited company",
  location: "United Kingdom / remote-first",
  products: "Business planning workshops, launch strategy, digital templates, and ongoing advisory retainers.",
  revenueModel: "Project fees, packaged services, and monthly retainers.",
  targetClient: "Early-stage founders, freelancers, and small service businesses who need structured planning before investing in marketing or operations.",
  clientPainPoints: "Unclear offer, inconsistent leads, lack of confidence in pricing, scattered planning documents, and too many competing priorities.",
  clientOutcome: "A focused plan, clear next actions, and a more professional story to share with customers, lenders, or partners.",
  marketingChannels: "SEO content, LinkedIn posts, referral partners, email newsletter, and downloadable lead magnets.",
  launchOffer: "A fixed-price Startup Clarity Sprint that creates the first business plan and 90-day launch roadmap.",
  pricingStrategy: "Start with accessible packaged pricing, then introduce premium advisory support as demand grows.",
  competitors: "Generic template sellers, local consultants, online courses, and do-it-yourself planning resources.",
  competitiveAdvantage: "A practical planning system that turns founder ideas into investor-ready and action-ready documents without bloated software.",
  strengths: "Clear positioning, low overheads, founder expertise, fast delivery, and reusable planning assets.",
  weaknesses: "Limited brand awareness at launch and reliance on founder-led sales.",
  opportunities: "Growing demand for lean startup support, digital templates, and specialist advisory services.",
  threats: "Low-cost template competitors, changing marketing channels, and slower client buying cycles.",
  startupCosts: "Website and brand: £1,800\nSoftware setup: £600\nLegal and accounting: £900\nLaunch marketing: £2,000\nContingency: £1,200",
  monthlyCosts: "Software: £250\nMarketing: £600\nProfessional fees: £200\nInsurance: £80\nFounder salary reserve: £2,000",
  fundingNeeded: "£8,000 initial launch budget plus three months operating runway.",
  revenueGoal: "Reach £8,000 monthly revenue by month six through a mix of projects and retainers.",
  milestones: "Month 1: Finalise offer and brand\nMonth 2: Launch website and lead magnet\nMonth 3: Secure first five clients\nMonth 6: Reach consistent monthly revenue target\nMonth 12: Add scalable template products",
  smartGoals: "Specific: Sell 20 Startup Clarity Sprints in 6 months\nMeasurable: Track discovery calls, proposals, and closed deals\nAchievable: Use weekly content and referrals\nRelevant: Validates the core offer\nTime-bound: Review at the end of month six",
  team: "Founder: strategy, delivery, sales\nFreelance designer: brand and templates\nAccountant: finance and compliance\nAdvisor: commercial review",
  actionChecklist: "Validate the offer with 10 conversations\nCreate service page and lead magnet\nSet up email capture\nPublish first 6 educational articles\nBuild referral partner list\nReview pricing after first 5 clients",
  closingNote: "Thank you for reviewing this plan. The next step is to validate the offer, secure early customers, and update this document with real market feedback.",
});